import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { btnPrimary, btnSecondary, easeOut } from "./primitives";
import HeroDashboard from "./HeroDashboard";

const TRUST = [
  "Factory Direct Purchasing",
  "Verified Turkish Manufacturers",
  "Large Volume Orders",
  "One Workspace",
];

export default function Hero({ onOpenLead }) {
  return (
    <section id="top" className="relative overflow-hidden pt-28 pb-16 sm:pt-32">
      <div className="pointer-events-none absolute inset-0 grid-bg radial-fade" aria-hidden />
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[900px] -translate-x-1/2 rounded-full bg-[#0052FF]/[0.07] blur-[120px]"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOut }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 shadow-sm backdrop-blur"
          >
            <span className="font-mono-ui text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0052FF]">
              Workspace Product
            </span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span className="text-[11px] font-medium text-[#64748B]">DeMaxtore Platform</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.05 }}
            className="font-display mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0A1128] sm:text-6xl lg:text-7xl"
          >
            BulkContainer<sup className="ml-1 align-super text-2xl text-[#94A3B8] sm:text-3xl">™</sup>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.12 }}
            className="font-display mx-auto mt-5 max-w-xl text-xl font-semibold text-[#0A1128] sm:text-2xl"
          >
            Bulk Purchasing.{" "}
            <span className="text-[#0052FF]">Factory Direct.</span> One Workspace.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.18 }}
            className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#64748B] sm:text-lg"
          >
            Source full-container and bulk quantities directly from verified Turkish
            manufacturers while managing every procurement step inside one connected Workspace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.24 }}
            className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => onOpenLead("create_bulkcontainer")}
              className={btnPrimary}
            >
              Create Your BulkContainer™ <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => onOpenLead("create_workspace")}
              className={btnSecondary}
            >
              Create Your Free Workspace
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.34 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          >
            {TRUST.map((t) => (
              <span key={t} className="flex items-center gap-1.5 text-[13px] font-medium text-[#475569]">
                <Check className="h-3.5 w-3.5 text-[#10B981]" strokeWidth={3} />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 0.3 }}
          className="relative mx-auto mt-16 max-w-5xl"
        >
          <div
            className="pointer-events-none absolute -inset-x-8 -top-8 bottom-0 rounded-[2rem] bg-[#0052FF]/[0.06] blur-2xl"
            aria-hidden
          />
          <div className="relative">
            <HeroDashboard />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
